import { cord, cord3, formatInput, gql2 } from "@/gql/lib";
import { Subject, InvEntityType } from "../data/model";
import { fixTier3XY, correctTier12XY } from "@/gql/cords";
import {
  ActionEnum,
  AxisType,
  TimeAxisRangeInput,
  targetAxis,
} from "@/gql/types";
import { POPULATION_TYPE, PopulationTypeHelper } from "@/common/models";
import { SharedState } from "@/common/state";
import { UseModal } from "@/components/root/modal";
import EntitiesTableModalVue from "@/components/modals/entities-table-modal/EntitiesTableModal.vue";
import { EntitiesModalProps } from "@/components/modals/entities-table-modal/model";
import { TimeFrameOption } from "@/common/state/models";
import { get_entities_modal_target_axis } from "./common";

const summary_sentence = (subject: Subject) => {
  switch (subject.type) {
    case POPULATION_TYPE.EMPLOYEE:
      return `Employees that received information from ${subject.name}`;
    case POPULATION_TYPE.LABEL:
      return `Employees that received ${subject.name}`;
    default:
      return "?";
  }
};

const get_summary = gql2<
  { subject: Subject; relative_time_frame: TimeFrameOption },
  { count: number; relative_count: number; type: InvEntityType; desc: string }
>(
  "inv-counters.employee-receivers",
  ({ subject, relative_time_frame }, { frame }) => {
    const cords = (time_frame: TimeAxisRangeInput) =>
      correctTier12XY({
        coordinate: {
          x: cord(AxisType.EmployeeReceiverType),
          y: cord(PopulationTypeHelper.asAxisType2(subject.type, "sender"), [
            subject.id,
          ]),
          z: time_frame,
        },
        action: ActionEnum.UniqCount,
      });
    return {
      query: `{
     delta: tier1count(params:${formatInput(cords(frame))}){
        count
        uniqs
     }
     relative: tier1count(params:${formatInput(
       cords({
         type: relative_time_frame.type,
         values: [relative_time_frame.from],
       })
     )}){
      count
      uniqs
   }
    }`,
      mapper: (res) => {
        const { delta, relative } = res;
        return {
          type: "EMPLOYEE_RECEIVER",
          count: delta.length > 0 ? delta[0].uniqs : 0,
          relative_count: relative.length > 0 ? relative[0].uniqs : 0,
          desc: summary_sentence(subject),
        };
      },
    };
  }
);

const get_titles = (subject: Subject) => {
  if (subject.type == POPULATION_TYPE.LABEL) {
    const label_name = subject.name.replace(/information/i, "");
    return {
      main: `<b>Employees</b> that received <b>${label_name}</b> information`,
      events: (name: string) =>
        `Events were <b>${name}</b> received <b>${label_name}</b> information`,
      files: (name: string) =>
        `<b>${label_name}</b> information files that were received by <b>${name}</b>`,
    };
  } else {
    return {
      main: `<b>Employees</b> that received information from <b>${subject.name}</b>`,
      events: (name: string) =>
        `Events were <b>${name}</b> sent information to <b>${subject.name}</b>`,
      files: (name: string) =>
        `File that were sent by ${name} to <b>${subject.name}</b>`,
    };
  }
};

const _get_main_cords = (subject: Subject) => {
  const { from, type } = SharedState.selectedTimeFrameOption.value;
  return correctTier12XY({
    coordinate: {
      x: cord(AxisType.EmployeeReceiver),
      y: cord(PopulationTypeHelper.asAxisType2(subject.type, "sender"), [
        subject.id,
      ]),
      z: {
        type,
        values: [from],
      },
    },
  }).coordinate;
};

const _get_events_cords = (subject: Subject, sub_subject_id: string) => {
  const { from, type } = SharedState.selectedTimeFrameOption.value;
  return fixTier3XY({
    x: cord3(AxisType.EmployeeReceiver, [sub_subject_id]),
    y: cord3(PopulationTypeHelper.asAxisType2(subject.type, "sender"), [
      subject.id,
    ]),
    z: {
      type,
      values: [from],
    },
  });
};

const show_modal = async (subject: Subject) => {
  const cord = _get_main_cords(subject);
  const titles = get_titles(subject);
  const props: EntitiesModalProps = {
    title: titles.main,
    type: POPULATION_TYPE.EMPLOYEE,
    info_types: [],
    target_axis: get_entities_modal_target_axis(subject.id, cord), //targetAxis.X,
    coordinate: cord,
    files_title: (entity) => titles.files(entity.name),
    events: {
      coordinate: (entity) => {
        return _get_events_cords(subject, entity.id);
      },
      title: (entity) => titles.events(entity.name),
    },
  };
  UseModal().show(EntitiesTableModalVue, props);
};

export default { get_summary, show_modal };
